import { useState } from "react";
import { Stethoscope, Building2, Hospital, Network, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

/**
 * Semble-inspired "Built for every scale" tabs.
 * One panel per audience with a highlight metric + three benefit lines.
 */

type Tier = {
  id: string;
  icon: typeof Stethoscope;
  label: { en: string; bn: string };
  headline: { en: string; bn: string };
  bullets: { en: string; bn: string }[];
  metric: { value: string; caption: { en: string; bn: string } };
  storyHref: string;
  storyLabel: { en: string; bn: string };
};

const TIERS: Tier[] = [
  {
    id: "solo",
    icon: Stethoscope,
    label: { en: "Solo doctors", bn: "একক ডাক্তার" },
    headline: { en: "Focus on patients, not paperwork", bn: "কাগজপত্র নয়, রোগীর দিকে মনোযোগ" },
    bullets: [
      { en: "Consult, prescribe and bill in one screen", bn: "পরামর্শ, প্রেসক্রিপশন ও বিলিং একই স্ক্রিনে" },
      { en: "Automatic WhatsApp reminders & follow-ups", bn: "স্বয়ংক্রিয় WhatsApp রিমাইন্ডার ও ফলো-আপ" },
      { en: "Work offline — sync when you're back online", bn: "অফলাইনে কাজ — অনলাইনে এলে সিঙ্ক" },
    ],
    metric: { value: "1 day/week", caption: { en: "saved on admin per doctor", bn: "প্রতি ডাক্তারের প্রশাসনিক সময় বাঁচে" } },
    storyHref: "/stories",
    storyLabel: { en: "Read Dr. Rahim's story", bn: "ডা. রহিমের গল্প পড়ুন" },
  },
  {
    id: "chamber",
    icon: Building2,
    label: { en: "Multi-chamber doctors", bn: "মাল্টি-চেম্বার ডাক্তার" },
    headline: { en: "More bookings, less chasing", bn: "বেশি বুকিং, কম দৌড়ানো" },
    bullets: [
      { en: "One calendar across every chamber & staff", bn: "সব চেম্বার ও স্টাফের জন্য একই ক্যালেন্ডার" },
      { en: "Live queue kiosk with 3-tone chime & Bengali TTS", bn: "লাইভ কিউ কিয়স্ক — ৩-টোন চাইম ও বাংলা TTS" },
      { en: "Real-time cashflow across bKash, Nagad & card", bn: "bKash, নগদ ও কার্ডের রিয়েল-টাইম হিসাব" },
    ],
    metric: { value: "1 → 7", caption: { en: "chambers scaled without extra admin", bn: "অতিরিক্ত অ্যাডমিন ছাড়া চেম্বার বৃদ্ধি" } },
    storyHref: "/stories",
    storyLabel: { en: "How Green Valley scaled", bn: "গ্রীন ভ্যালি কীভাবে বাড়ল" },
  },
  {
    id: "group",
    icon: Network,
    label: { en: "Specialty groups", bn: "স্পেশালিটি গ্রুপ" },
    headline: { en: "Standardise care across sites", bn: "সব ব্রাঞ্চে একই মানের সেবা" },
    bullets: [
      { en: "Shared Rx templates & clinical guidelines", bn: "শেয়ার্ড Rx টেমপ্লেট ও ক্লিনিক্যাল গাইডলাইন" },
      { en: "Role-based access with full audit trail", bn: "ভূমিকাভিত্তিক অ্যাক্সেস ও অডিট ট্রেইল" },
      { en: "Group analytics: revenue, wait, outcomes", bn: "গ্রুপ অ্যানালিটিক্স: আয়, অপেক্ষা, ফলাফল" },
    ],
    metric: { value: "12% → 4%", caption: { en: "no-shows after auto reminders", bn: "অটো রিমাইন্ডারে অনুপস্থিতি কমেছে" } },
    storyHref: "/stories",
    storyLabel: { en: "See group case study", bn: "গ্রুপ কেস স্টাডি দেখুন" },
  },
  {
    id: "hospital",
    icon: Hospital,
    label: { en: "Hospitals & enterprise", bn: "হাসপাতাল ও এন্টারপ্রাইজ" },
    headline: { en: "Modernise without downtime", bn: "ডাউনটাইম ছাড়াই আধুনিকায়ন" },
    bullets: [
      { en: "SSO, SAML & hospital-grade security controls", bn: "SSO, SAML ও হাসপাতাল-মানের নিরাপত্তা" },
      { en: "Custom API access for HIS / lab / pharmacy", bn: "HIS / ল্যাব / ফার্মেসির জন্য কাস্টম API" },
      { en: "99.99% uptime on Lovable Cloud infrastructure", bn: "৯৯.৯৯% আপটাইম" },
    ],
    metric: { value: "99.99%", caption: { en: "uptime you can depend on", bn: "নির্ভরযোগ্য আপটাইম" } },
    storyHref: "/security",
    storyLabel: { en: "See our security posture", bn: "নিরাপত্তা কাঠামো দেখুন" },
  },
];

export default function AudienceScaleSection() {
  const { lang } = useLang();
  const [active, setActive] = useState<string>(TIERS[0].id);
  const tier = TIERS.find((t) => t.id === active) ?? TIERS[0];
  const Icon = tier.icon;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1ba179] px-3 py-1.5 rounded-full bg-[#e6faf3] border border-[#c7f1e2] mb-5">
            {lang === "bn" ? "প্রতিটি আকারের জন্য" : "Built for every scale"}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a]">
            {lang === "bn"
              ? "একজন ডাক্তার থেকে পুরো হাসপাতাল — এক প্ল্যাটফর্ম"
              : "From a single doctor to a whole hospital — one platform"}
          </h2>
        </div>

        {/* Tab chips */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {TIERS.map((t) => {
            const TIcon = t.icon;
            const on = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                  on
                    ? "bg-[#1ba179] text-white border-[#1ba179] shadow-[0_6px_18px_-8px_rgba(37,195,154,0.6)]"
                    : "bg-white text-[#334155] border-[#e2e8f0] hover:border-[#1ba179] hover:text-[#1ba179]"
                }`}
              >
                <TIcon className="w-4 h-4" />
                {lang === "bn" ? t.label.bn : t.label.en}
              </button>
            );
          })}
        </div>

        {/* Active panel */}
        <div
          key={tier.id}
          className="grid md:grid-cols-[1.2fr_1fr] gap-6 rounded-3xl border border-[#e2e8f0] bg-gradient-to-br from-[#f8fafc] to-white p-6 md:p-10 animate-fade-in"
        >
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1ba179] mb-4">
              <Icon className="w-4 h-4" />
              {lang === "bn" ? tier.label.bn : tier.label.en}
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-[#0f172a] leading-tight mb-5">
              {lang === "bn" ? tier.headline.bn : tier.headline.en}
            </h3>
            <ul className="space-y-3 mb-6">
              {tier.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] text-[#334155]">
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-[#1ba179] mt-0.5" />
                  <span>{lang === "bn" ? b.bn : b.en}</span>
                </li>
              ))}
            </ul>
            <a
              href={tier.storyHref}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1ba179] hover:gap-2.5 transition-all"
            >
              {lang === "bn" ? tier.storyLabel.bn : tier.storyLabel.en}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="relative rounded-2xl bg-white border border-[#e2e8f0] p-6 flex flex-col justify-center overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#25c39a]/10 blur-3xl" />
            <div className="relative">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748b] mb-2">
                {lang === "bn" ? "ফলাফল" : "Outcome"}
              </div>
              <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-[#0f172a] to-[#1ba179] bg-clip-text text-transparent tracking-tight">
                {tier.metric.value}
              </div>
              <div className="mt-3 text-sm text-[#475569] leading-relaxed">
                {lang === "bn" ? tier.metric.caption.bn : tier.metric.caption.en}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
